import _ from 'lodash';
import Combinatorics from 'js-combinatorics';

import { STATS } from 'shared/stats';
import {
  SLOT_MAP,
  SOCKET_TYPES,
} from 'shared/constants';

import {
  getSpecInfo,
  getSpecInfoById,
  getCharacterSpecInfo,
} from 'shared/character';

import {
  getEnchant,
  getEnchants,
  getEnchantableSlots,
} from 'shared/enchants';
import {
  getGemInfo,
  getGemDetails,
  isGemUnique,
} from 'shared/gems';
import GameData from 'shared/gameData';
import { addTag } from 'shared/itemTags';

export const slotMap = SLOT_MAP;

export const qualityNameMap = {
  0: 'poor',
  1: 'common',
  2: 'uncommon',
  3: 'rare',
  4: 'epic',
  5: 'legendary',
  6: 'artifact',
  7: 'heirloom',
};

export const FISHING_POLE = {
  itemClass: 2,
  itemSubClass: 20,
};

export const INVALID_WEAPON_TYPES = [{
  itemClass: 2,
  itemSubClass: 14,
}];

const invTypeToHands = {
  13: '1h', // Weapon
  14: 'oh', // Shield
  15: '2h', // Ranged (Bow, XBow, Gun)
  17: '2h', // Two-Hand
  21: 'mh', // Main Hand
  22: 'oh', // Off Hand
  23: 'oh', // Holdable (Tome)
  26: 'mh', // MH Ranged (Wand / Gun)

  // 25 - Thrown (removed)
  // 27 - Quiver
  // 28 - Relic
};


// overrides for a few cases
const weaponClassDetailsRaw = [{
  // 1h axe
  itemClass: 2,
  itemSubClass: 0,
  specsCanDrop: [
    // str
    ['paladin', 'protection'],
    ['warrior', 'protection'],
    ['warrior', 'fury'],
    ['deathknight', 'frost'],

    // int
    ['paladin', 'holy'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['monk', 'mistweaver'],
    ['evoker', 'augmentation'],
    ['evoker', 'devastation'],
    ['evoker', 'preservation'],

    // agi
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'assassination'],
  ],
}, {
  // 2h axe
  itemClass: 2,
  itemSubClass: 1,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
    ['hunter', 'survival'],
    ['evoker', 'augmentation'],
    ['evoker', 'devastation'],
    ['evoker', 'preservation'],
  ],
}, {
  // gun
  itemClass: 2,
  itemSubClass: 2,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // gun
  itemClass: 2,
  itemSubClass: 3,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // 1h mace
  itemClass: 2,
  itemSubClass: 4,
  specsCanDrop: [
    // tanks
    ['paladin', 'protection'],
    ['warrior', 'fury'],
    ['warrior', 'protection'],
    ['deathknight', 'frost'],

    // casters/healers
    ['paladin', 'holy'],

    ['druid', 'balance'],
    ['druid', 'restoration'],

    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],

    ['shaman', 'elemental'],
    ['shaman', 'restoration'],

    ['monk', 'mistweaver'],

    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // agi
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
}, {
  // 2h mace
  itemClass: 2,
  itemSubClass: 5,
  specsCanDrop: [
    ['paladin', 'retribution'],

    ['warrior', 'fury'],
    ['warrior', 'arms'],

    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],

    ['shaman', 'elemental'],

    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['druid', 'feral'],
    ['druid', 'guardian'],

    ['evoker', 'preservation'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
  ],
}, {
  // polearm
  itemClass: 2,
  itemSubClass: 6,
  specsCanDrop: [
    // agi
    ['druid', 'feral'],
    ['druid', 'guardian'],
    ['hunter', 'survival'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],

    // str
    ['paladin', 'retribution'],
    ['warrior', 'arms'],
    ['warrior', 'fury'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
  ],
  specsAlsoCanUse: [
    ['druid', 'balance'],
  ],
}, {
  // 1h sword
  itemClass: 2,
  itemSubClass: 7,
  specsCanDrop: [
    // tank
    ['paladin', 'protection'],
    ['warrior', 'fury'],
    ['warrior', 'protection'],
    ['deathknight', 'frost'],

    // agi sword
    ['rogue', 'outlaw'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // int sword
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['paladin', 'holy'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'assassination'],
  ],
}, {
  // 2h sword
  itemClass: 2,
  itemSubClass: 8,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
    ['hunter', 'survival'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],
  ],
}, {
  // warglaive
  itemClass: 2,
  itemSubClass: 9,
  specsCanDrop: [
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
  ],
}, {
  // staff
  itemClass: 2,
  itemSubClass: 10,
  specsCanDrop: [
    // int
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // agi
    ['druid', 'feral'],
    ['druid', 'guardian'],
    ['hunter', 'survival'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
  specsAlsoCanUse: [
    ['warrior', 'arms'],
    ['warrior', 'fury'],
  ],
}, {
  // fist
  itemClass: 2,
  itemSubClass: 13,
  specsCanDrop: [
    // agi
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],

    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    ['druid', 'balance'],
    ['druid', 'restoration'],

    ['warrior', 'fury'],
    ['warrior', 'protection'],
  ],
  specsAlsoCanUse: [
    ['shaman', 'elemental'],
  ],
}, {
  // dagger
  itemClass: 2,
  itemSubClass: 15,
  specsCanDrop: [
    // int
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],

    // agi
    ['rogue', 'assassination'],
    ['rogue', 'subtlety'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'outlaw'],
  ],
}, {
  // bow/crossbow
  itemClass: 2,
  itemSubClass: 18,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // wand
  itemClass: 2,
  itemSubClass: 19,
  hand: 'mh',
  specsCanDrop: [
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
  ],
}, {
  // misc (off hands, tomes)
  itemClass: 4,
  itemSubClass: 0,
  hand: 'oh',
  noEnchant: true,
  specsCanDrop: [
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['paladin', 'holy'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
    ['evoker', 'devastation'],
    ['evoker', 'augmentation'],
    ['evoker', 'preservation'],
  ],
  specsAlsoCanUse: [
    ['shaman', 'elemental'],
  ],
}, {
  // shield
  itemClass: 4,
  itemSubClass: 6,
  hand: 'oh',
  noEnchant: true,
  specsCanDrop: [
    ['paladin', 'holy'],
    ['paladin', 'protection'],
    ['warrior', 'protection'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
  ],
}];

export const armorSubClasses = [{
  itemSubClass: 0,
  key: 'misc',
  name: 'Misc',
}, {
  itemSubClass: 1,
  key: 'cloth',
  name: 'Cloth',
}, {
  itemSubClass: 2,
  key: 'leather',
  name: 'Leather',
}, {
  itemSubClass: 3,
  key: 'mail',
  name: 'Mail',
}, {
  itemSubClass: 4,
  key: 'plate',
  name: 'Plate',
}, {
  itemSubClass: 6,
  key: 'shield',
  name: 'Shield',
}];

export const invertedQualityNameMap = _.mapValues(_.invert(qualityNameMap), parseInt);

export const invertedSlotMap = _.mapValues(_.invert(slotMap), slotId => parseInt(slotId, 10));

const MAIN_HAND_SLOT = invertedSlotMap.main_hand;

// ItemRedundancySlot
// eslint-disable-next-line max-len
// https://github.com/tomrus88/BlizzardInterfaceCode/blob/master/Interface/AddOns/Blizzard_APIDocumentationGenerated/ItemConstants_MainlineDocumentation.lua
const discountSlotMap = {
  head: 0,
  neck: 1,
  shoulder: 2,
  chest: 3,
  waist: 4,
  legs: 5,
  feet: 6,
  wrist: 7,
  hands: 8,
  finger: 9,
  trinket: 10,
  back: 11,
  twohand: 12,
  mainhand: 13,
  onehandweapon: 14,
  onehandweaponsecond: 15,
  offhand: 16,
};

const twoHandFallbackSlots = [
  'mainhand',
  'onehandweapon',
  'onehandweaponsecond',
  'offhand',
];

const ignoreTwoHandFallbackSlotSpecs = [
  66, // prot pally
];

const mainStatMap = {
  agi: ['STAT_AGILITY', 'STAT_AGI_INT', 'STAT_STR_AGI', 'STAT_STR_AGI_INT'],
  int: ['STAT_INTELLECT', 'STAT_AGI_INT', 'STAT_STR_INT', 'STAT_STR_AGI_INT'],
  str: ['STAT_STRENGTH', 'STAT_STR_AGI', 'STAT_STR_INT', 'STAT_STR_AGI_INT'],
};

export const secondaryBonuses = [{
  bonusId: 603,
  stat: 'crit',
  name: 'Crit',
}, {
  bonusId: 604,
  stat: 'haste',
  name: 'Haste',
}, {
  bonusId: 605,
  stat: 'mastery',
  name: 'Mastery',
}, {
  bonusId: 607,
  stat: 'vers',
  name: 'Versatility',
}];

const tertiaryBonusIds = [
  40,
  41,
  42,
  43,
];

export const CRAFTED_STATS = [{
  id: 32,
  name: 'crit',
}, {
  id: 36,
  name: 'haste',
}, {
  id: 49,
  name: 'mastery',
}, {
  id: 40,
  name: 'vers',
}];

export const EMBELLISHED_REAGENT_SLOT_IDS = [
  // Dragonflight
  112,
  123,
  178,
  179,
  180,
  226,
  233,

  // The War Within
  //202, // Darkmoon Sigils?
  281,
  312,
  334,
  340,
  341,
];

const RECRAFT_STAT_SLOTS = [
  // Dragonflight
  125,
  224,
  225,
  227,

  // The War Within
  243,
  282,
  283,
  365,
];

// TODO: Figure out where this is encoded in game data
// ItemBonusListGroup 296 looks to collect these in 10.1 but I can't figure out a map back to crafting
export const professionCraftingQualities = [{
  tier: 1,
  levelOffset: 0,
  bonusId: 9623,
}, {
  tier: 2,
  levelOffset: 3,
  bonusId: 9624,
}, {
  tier: 3,
  levelOffset: 6,
  bonusId: 9625,
}, {
  tier: 4,
  levelOffset: 9,
  bonusId: 9626,
}, {
  tier: 5,
  levelOffset: 13,
  bonusId: 9627,
}];

const CRAFTING_LEVEL_UPGRADE_SLOTS = [
  // Dragonflight
  111, // Infuse with Power
  116, // Empower with Training Matrix
  126, // Infuse with Power
  189, // Empower with Training Matrix
  247, // Spark

  // The War Within
  255, // Competitor's Heraldy (PVP)
  256, // Spark
  284, // Infuse with Power
  342, // Empower
  366, // Infuse with Power
  368, // Empower
];

const CRAFTING_SLOT_REMOVE_SOCKETS = [
  247, // any Spark craft will remove sockets since it's changing seasons
  256, // TWW spark slot
];

// For small, extra ilevel bumps from Augment items
// Like War Within Season 2 Turbo Boost
// https://www.wowhead.com/guide/the-war-within/turbo-boost-dinar-event-overview
const CRAFTING_LEVEL_AUGMENT_SLOTS = [
  384, // Augment Beyond
];

// map each crafted item back to the main crest
export const CRAFTING_CURRENCY_ALTERNATE = {

  // TWW Season 2 Turbo Boost

  // Gilded Augmentation Matrix
  239146: {
    id: 3110,
    amount: 30,
    type: 'currency',
    name: 'Gilded Undermine Crest',
    icon: 'inv_crestupgrade_undermine_gilded',
  },

  // Runed Augmentation Matrix
  239203: {
    id: 3109,
    amount: 30,
    type: 'currency',
    name: 'Runed Undermine Crest',
    icon: 'inv_crestupgrade_undermine_runed',
  },

  // Enchanted Weathered Undermine Crest
  230937: {
    id: 3107,
    amount: 30,
    type: 'currency',
    name: 'Weathered Undermine Crest',
    icon: 'inv_crestupgrade_undermine_weathered',
  },
  // Enchanted Runed Undermine Crest
  230936: {
    id: 3109,
    amount: 45,
    type: 'currency',
    name: 'Runed Undermine Crest',
    icon: 'inv_crestupgrade_undermine_runed',
  },
  // Enchanted Gilded Undermine Crest
  230935: {
    id: 3110,
    amount: 60,
    type: 'currency',
    name: 'Gilded Undermine Crest',
    icon: 'inv_crestupgrade_undermine_gilded',
  },

  // Enchanted Weathered Harbinger Crest
  224069: {
    id: 2914,
    amount: 30,
    type: 'currency',
    name: 'Weathered Harbinger Crest',
    icon: 'inv_crestupgrade_xalatath_weathered',
  },
  // Enchanted Runed Harbinger Crest
  224072: {
    id: 2916,
    amount: 45,
    type: 'currency',
    name: 'Runed Harbinger Crest',
    icon: 'inv_crestupgrade_xalatath_runed',
  },
  // Enchanted Gilded Harbinger Crest
  224073: {
    id: 2917,
    amount: 60, // 11.0.7
    type: 'currency',
    name: 'Gilded Harbinger Crest',
    icon: 'inv_crestupgrade_xalatath_gilded',
  },
};

export const WARBAND_CREST_DISCOUNT_AMOUNT = 1 / 3;

export const WARBAND_CREST_DISCOUNT_ACHIEVEMENT = {
  // currencyId:achievementId
  // Achievement name pattern has been like "Gilded of the Undermine" or "Harbinger of the Gilded"

  // 11.1
  3107: 40942, // Weathered
  3108: 40943, // Carved
  3109: 40944, // Runed
  3110: 40945, // Gilded

  // 11.0
  2914: 40107, // Weathered
  2915: 40115, // Carved
  2916: 40118, // Runed
  2917: 40939, // Gilded
};

const seasonUpgradeNames = new Set([
  'Explorer',
  'Adventurer',
  'Veteran',
  'Champion',
  'Hero',
  'Myth',
]);

const ROLLABLE_SOCKET_TYPES = [
  SOCKET_TYPES.PRISMATIC,
];

const ITEMS_NO_DYNAMIC_SOCKET = [
  // pocket-sized computation device
  167555,
];

// max sockets on one piece of gear
export const MAX_ITEM_SOCKETS = 3;

// Seasonal maximum sockets per slot
export const SLOT_SOCKETS = {
  neck: 2,
  finger: 2,
  head: 1,
  wrist: 1,
  waist: 1,
};

// slots that can always get sockets from crafting, not gated by drop chance or limited currency
export const SLOT_SOCKETS_UNGATED = [
  'neck',
  'finger',
  'finger1',
  'finger2',
];

// max sockets on a valid gearset
// Ignores PVP sockets from The War Within
export const MAX_TOTAL_SOCKETS = _.reduce(SLOT_SOCKETS, (res, num, slot) => {
  if (slot === 'finger' || slot === 'trinket') {
    return res + (2 * num);
  }
  return (res + num);
}, 0);

export const SOCKET_DISABLE_BONUSES = [
  7756, // Timewarped
];


const ITEM_CONVERSION_EXCLUDE_ITEM_IDS = new Set([
  204465, // Voice of the Silent Star
]);


const HIGH_SECONDARY_PROC_TRINKETS = [
  179350, // IQD
  207581, // Mirror of Fractured Tomorrow
];

// Items that have special bonus ID variations. Used for item mutation for Top Gear and Droptimizer
const ITEM_BONUS_VARIATIONS = [{
  // Unbound Changeling
  itemId: 178708,
  variations: [{
    bonusId: 6915,
    name: 'all stats proc',
  }, {
    bonusId: 6916,
    name: 'crit proc',
  }, {
    bonusId: 6917,
    name: 'haste proc',
  }, {
    bonusId: 6918,
    name: 'mastery proc',
  }],
}];

const ITEM_VARIATION_BONUS_IDS = new Set(_.map(_.flatten(_.map(ITEM_BONUS_VARIATIONS, 'variations')), 'bonusId'));

export const getCurrentSeason = _.memoize(() => {
  return _.find(GameData.Seasons, { active: true });
});
export const getCurrentSeasonId = _.memoize(() => {
  return getCurrentSeason().id;
});

//////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// Item helpers
//
//////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const itemToSimc = (item, slot = null) => {
  const generalSlotMap = {
    finger: 'finger1',
    trinket: 'trinket1',
  };
  let slotFinal = slot || slotMap[item.inventoryType];
  if (generalSlotMap[slotFinal]) {
    slotFinal = generalSlotMap[slotFinal];
  }
  const attrs = {
    id: item.id,
    bonus_id: item.bonus_id,
    gem_id: item.gem_id,
    enchant_id: item.enchant_id,
    crafted_stats: item.crafted_stats,
    titan_disc_id: item.titan_disc_id,
  };
  const attrStr = _.join(_.map(_.pickBy(attrs), (val, key) => _.join([key, val], '=')), ',');

  return `${slotFinal}=,${attrStr}`;
};


//////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// Bonus ID Helpers
//
//////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getBonusIds = item => {
  if (item.bonus_id) {
    return _.map(_.filter(_.split(item.bonus_id, '/')), bid => parseInt(bid, 10));
  }

  if (item.bonusLists) {
    return item.bonusLists;
  }

  return [];
};

export const hasBonusId = (item, bonusId) => {
  return _.includes(getBonusIds(item), bonusId);
};

export const setBonusIds = (item, bonusIdArray) => {
  const cleanArray = _.filter(bonusIdArray);
  return {
    ...item,
    bonus_id: _.join(cleanArray, '/'),
    bonusLists: cleanArray,
  };
};

export const addBonusIds = (item, bonusIds) => {
  if (!item || !bonusIds) {
    return item;
  }
  const origIds = getBonusIds(item);
  const bonusIdsArray = !_.isArray(bonusIds) ? [bonusIds] : bonusIds;
  return setBonusIds(item, _.uniq([...origIds, ...bonusIdsArray]));
};

export const removeBonusIds = (item, bonusIds) => {
  if (!item || !bonusIds) {
    return item;
  }
  const origIds = getBonusIds(item);
  const bonusIdsArray = !_.isArray(bonusIds) ? [bonusIds] : bonusIds;
  return setBonusIds(item, _.without(origIds, ...bonusIdsArray));
};

export const isItemLevelBonus = (bonusId) => {
  return !_.isUndefined(GameData.BonusLevels[bonusId]);
};

export const isBaseLevelBonus = (bonusId) => {
  return !_.isUndefined(GameData.BonusBaseLevels[bonusId]);
};

export const isSocketBonus = (bonusId) => {
  return !_.isUndefined(GameData.BonusSockets[bonusId]);
};

export const isItemVariationBonus = bonusId => {
  return ITEM_VARIATION_BONUS_IDS.has(bonusId);
};

export const getItemLevelAdjustBonuses = (bonusIdArray) => {
  return _.filter(bonusIdArray, isItemLevelBonus);
};
export const getItemLevelAdjust = (bonusIdArray) => {
  return _.sum(_.map(getItemLevelAdjustBonuses(bonusIdArray), bid => GameData.BonusLevels[bid]));
};

export const getBonusForLevelOffset = (offset) => {
  return parseInt((_.findKey(GameData.BonusLevels, o => o === offset)), 10);
};

export const getItemBonusForDelta = levelDelta => GameData.BonusLevelDeltas[levelDelta];

export const getBaseLevelFromBonuses = bonusIds => {
  const baseLevelBonuses = _.filter(_.map(bonusIds, bid => GameData.BonusBaseLevels[bid]));
  return _.minBy(baseLevelBonuses, 'base_level_priority')?.base_level || null;
};

export const getItemBaseLevel = item => {
  const bonusIds = getBonusIds(item);

  return getBaseLevelFromBonuses(bonusIds) || item.baseItemLevel || item.itemLevel;
};

export const removeItemLevelBonuses = (item) => {
  if (!item) {
    return item;
  }
  const origIds = getBonusIds(item);
  return setBonusIds(item, _.reject(origIds, isItemLevelBonus));
};

// HACK
// This sucks
// Need it to maintain the fact that an item has an augment matrix
const RETAIN_ITEM_LEVEL_BONUS_IDS = [
  12374,
  12373,
];

export const updateItemLevel = (item, newItemLevel) => {
  let newItem = { ...item };

  const newItemLevelInt = parseInt(newItemLevel, 10);

  const baseItemLevel = getItemBaseLevel(item);

  // remove item level bonuses
  let bonusIds = getBonusIds(newItem);
  const retainBonusIds = _.filter(
    bonusIds,
    bid => isItemLevelBonus(bid) && _.includes(RETAIN_ITEM_LEVEL_BONUS_IDS, bid),
  );
  bonusIds = _.reject(bonusIds, isItemLevelBonus);

  // update the base item level if there are bonus IDs with base_level
  const retainedOffset = getItemLevelAdjust(retainBonusIds) || 0;

  // determine offset
  const newOffset = newItemLevelInt - baseItemLevel - retainedOffset;

  // find correct bonus ID
  const newBonusId = GameData.BonusLevelDeltas[newOffset];

  bonusIds.push(parseInt(newBonusId, 10));
  _.each(retainBonusIds, bid => bonusIds.push(bid));

  newItem = setBonusIds(newItem, bonusIds);
  // save base item level if not already extant
  if (!newItem.baseItemLevel) {
    newItem.baseItemLevel = baseItemLevel || newItem.itemLevel;
  }
  newItem.itemLevel = newItemLevelInt;

  return newItem;
};

const getUpgradeBonusIds = _.memoize(() => {
  return new Set(_.map(_.flatten(_.values(GameData.BonusUpgradeSets)), 'bonusId'));
});

const getUpgradeSetArray = _.memoize(() => {
  return _.map(
    _.flatten(_.values(GameData.BonusUpgradeSets)),
    set => ({
      ...set,
      fullName: `${set.name} ${set.level}/${set.max}`,
    }),
  );
});

export const getUpgradeSet = upgradeSetId => _.filter(getUpgradeSetArray(), { group: upgradeSetId });
export const getUpgradeSetLevel = (upgradeSetId, level) => {
  return _.find(getUpgradeSet(upgradeSetId), { level });
};

let seasonBonusLevelLookup;
const getSeasonUpgradeSetsArray = _.memoize(seasonId => {
  return _.filter(getUpgradeSetArray(), upgradeSet => {
    return upgradeSet.seasonId === seasonId && seasonUpgradeNames.has(upgradeSet.name);
  });
});
const getSeasonBonusLevelLookup = () => {
  if (!seasonBonusLevelLookup) {
    seasonBonusLevelLookup = {};
    const upgradeBonuses = _.filter(getUpgradeSetArray(), 'seasonId');
    _.each(upgradeBonuses, upgrade => {
      const { seasonId } = upgrade;
      if (seasonId && seasonUpgradeNames.has(upgrade.name)) {
        if (!seasonBonusLevelLookup[seasonId]) {
          seasonBonusLevelLookup[seasonId] = {};
        }
        const cur = seasonBonusLevelLookup[seasonId][upgrade.itemLevel];
        // Use the lowest level in case the ID order doesn't match the actual upgrade track
        if (!cur || cur.level > upgrade.level) {
          seasonBonusLevelLookup[seasonId][upgrade.itemLevel] = upgrade;
        }
      }
    });
    seasonBonusLevelLookup = _.mapValues(seasonBonusLevelLookup, sbll => _.mapValues(sbll, 'bonusId'));
  }
  return seasonBonusLevelLookup;
};
export const getSeasonUpgrades = (seasonId) => {
  return getSeasonUpgradeSetsArray(seasonId);
};
export const getUpgradeSetForLevel = (seasonId, itemLevel) => {
  return _.findLast(getSeasonUpgradeSetsArray(seasonId), { itemLevel });
};
export const getUpgradesForLevel = (seasonId, itemLevel) => {
  const upgradeSet = getUpgradeSetForLevel(seasonId, itemLevel);
  if (upgradeSet) {
    const sets = _.filter(getUpgradeSetArray(), { group: upgradeSet.group });
    return _.filter(sets, set => set.itemLevel > itemLevel);
  }

  return null;
};

export const getSeasonBonusForLevel = (season, level) => {
  const lookup = getSeasonBonusLevelLookup();
  if (lookup[season]) {
    return seasonBonusLevelLookup[season][level] || null;
  }
  return null;
};

export const getSeasonMaxLevel = season => {
  const lookup = getSeasonBonusLevelLookup();
  return _.max(_.keys(lookup[season]));
};

let bonusUpgradeSetLookup;
export const getBonusUpgradeSet = (bonusId) => {
  if (!bonusUpgradeSetLookup) {
    bonusUpgradeSetLookup = _.mapValues(
      _.keyBy(getUpgradeSetArray(), 'bonusId'),
      entry => GameData.BonusUpgradeSets[entry.group],
    );
  }

  return bonusUpgradeSetLookup[bonusId];
};

export const getUpgradeInfoForBonus = (bonusId) => {
  return _.find(getUpgradeSetArray(), { bonusId });
};
export const getSeasonUpgradeInfoForLevel = (seasonId, level) => {
  const upgradeBonus = getSeasonBonusForLevel(seasonId, level);
  return getUpgradeInfoForBonus(upgradeBonus);
};

export const getMaxUpgradeInfo = bonusId => {
  const upgradeSet = getBonusUpgradeSet(bonusId);
  const maxEntry = _.last(upgradeSet);
  if (maxEntry) {
    return getUpgradeInfoForBonus(maxEntry.bonusId);
  }

  return null;
};

export const getUpgradeBonusForLevel = (upgradeSetId, itemLevel) => {
  return _.find(GameData.BonusUpgradeSets[upgradeSetId], { itemLevel })?.bonusId;
};

export const getItemUpgradeBonus = (item) => {
  const bonusIds = getBonusIds(item);

  return _.find(bonusIds, bid => getUpgradeBonusIds().has(bid));
};

export const applyItemUpgradeLevel = (item, upgradeBonus) => {
  let newItem = { ...item };

  const upgradeInfo = getUpgradeInfoForBonus(upgradeBonus);

  if (!upgradeInfo) {
    return newItem;
  }

  // remove current upgrade info
  newItem = removeBonusIds(newItem, getItemUpgradeBonus(item));

  newItem.upgrade = upgradeInfo;
  newItem = updateItemLevel(newItem, upgradeInfo.itemLevel);
  newItem = addBonusIds(newItem, upgradeInfo.bonusId);

  return newItem;
};

// updates the level offset and upgrade level bonus IDs
// Will keep the item within its current track
export const updateItemUpgradeLevel = (item, targetUpgradeBonus) => {
  if (!item || !item.upgrade) {
    return item;
  }

  let newItem = { ...item };

  const targetUpgradeInfo = getUpgradeInfoForBonus(targetUpgradeBonus);
  if (targetUpgradeInfo && targetUpgradeInfo.itemLevel > item.itemLevel) {
    const curUpgradeBonus = getItemUpgradeBonus(item);
    const curUpgradeSet = getBonusUpgradeSet(curUpgradeBonus);

    // see if item current track has an upgrade level for this item level
    let newUpgradeInfo = _.find(curUpgradeSet, { itemLevel: targetUpgradeInfo.itemLevel });
    if (!newUpgradeInfo) {
      // TODO: This _should_ be safe but should probably add some double checks here
      newUpgradeInfo = getMaxUpgradeInfo(curUpgradeBonus);
    }

    if (!newItem.upgradeOrig) {
      newItem.upgradeOrig = newItem.upgrade;
    }

    if (newUpgradeInfo) {
      newItem = applyItemUpgradeLevel(newItem, newUpgradeInfo.bonusId);
    }
  }

  return newItem;
};

export const getLevelSelectorSequence = selectorId => {
  return _.find(GameData.LevelSelectorSequences, { id: selectorId });
};
export const getLevelSelectorValue = (selectorId, index) => {
  const sequence = getLevelSelectorSequence(selectorId);
  if (sequence) {
    return sequence.levels[index];
  }
  return null;
};
export const getIndexForSequenceLevel = (selectorId, level) => {
  const sequence = getLevelSelectorSequence(selectorId);
  if (sequence) {
    return _.indexOf(sequence.levels, level);
  }
  return null;
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getItemSlotName = item => SLOT_MAP[item.inventoryType];

const weaponClassDetails = _.map(weaponClassDetailsRaw, details => {
  const specsCanDrop = _.map(details.specsCanDrop, spec => _.get(getSpecInfo(spec[0], spec[1]), 'id'));
  const specsCanUse = _.union(
    specsCanDrop,
    _.map(details.specsAlsoCanUse, spec => _.get(getSpecInfo(spec[0], spec[1]), 'id')),
  );
  return {
    ...details,
    specsCanDrop,
    specsCanUse,
  };
});

export const getWeaponHands = (weapon) => {
  // make sure we have an item
  if (!weapon || !weapon.itemClass) {
    return null;
  }
  // reject items that are not in the main inv type weapon list (filters out rings)
  if (!invTypeToHands[weapon.inventoryType]) {
    return null;
  }
  const override = _.find(weaponClassDetails, { itemClass: weapon.itemClass, itemSubClass: weapon.itemSubClass });
  return _.get(override, 'hand', invTypeToHands[weapon.inventoryType]);
};

export const isWeaponEnchantable = weapon => {
  const info = _.find(weaponClassDetails, { itemClass: weapon.itemClass, itemSubClass: weapon.itemSubClass });
  if (info && info.noEnchant) {
    return false;
  }
  if (weapon.quality === 6) {
    return false;
  }
  return true;
};

const mainStatIdMap = _.mapValues(
  mainStatMap,
  statEnums => _.map(statEnums, enumName => _.get(_.find(STATS, { enum: enumName }), 'dataId')),
);

const allMainStats = _.flatten(_.values(mainStatIdMap));

const itemHasAnyMainStat = item => {
  let hasStat = false;
  _.each(item.stats, stat => {
    hasStat = hasStat || _.includes(allMainStats, stat.id);
  });
  return hasStat;
};

const itemHasMainStat = (item, mainStatShortName) => {
  let hasStat = false;
  _.each(item.stats, stat => {
    hasStat = hasStat || _.includes(mainStatIdMap[mainStatShortName], stat.id);
  });
  return hasStat;
};

export const isCloak = item => {
  return item.itemClass === 4 && item.inventoryType === parseInt(invertedSlotMap.back, 10);
};

// Test if an item is usable or if it can drop
// This is private since it has the ugly boolean parameter
// Use the exported isItemUsable or doesItemDrop below
export const itemNotUsableReason = (item, classSearch, specSearch, onlyDrop = false, checkSpec = true) => {
  const specInfo = getSpecInfo(classSearch, specSearch);

  ///////////////////////////////////////////////////
  // check class/spec constraints first

  if (!specInfo) {
    return 'no_spec_info';
  }

  if (item.allowableClasses) {
    if (!_.includes(item.allowableClasses, specInfo.class.id)) {
      return 'class_cannot_use';
    }
  }

  // check spec override first
  if (checkSpec && item.specs) {
    if (!_.includes(item.specs, specInfo.id)) {
      return 'spec_cannot_use';
    }
  }

  ///////////////////////////////////////////////////
  // Now check various attributes of the armor itself

  let wrongMainStat = false;

  // check for non-matching main stat on an item
  if (item.stats && itemHasAnyMainStat(item) && !itemHasMainStat(item, specInfo.mainStat)) {
    wrongMainStat = true;
  }

  // jewelry is usable by anyone assuming no class/spec constraints
  if (_.includes(['finger', 'neck'], getItemSlotName(item))) {
    // if jewelry has a main stat, make sure it's the right one.
    // some very old jewelry showing up in refactored M+ dungeons have main stat
    // These will hopefully go away completely when Blizz updates the dungeon journal
    if (wrongMainStat) {
      return 'wrong_main_stat';
    }
    return null;
  }

  // check armor class
  if (item.itemClass === 4) {
    // skip cloaks, anyone can use
    if (item.inventoryType !== parseInt(invertedSlotMap.back, 10)) {
      const armorSubClass = _.find(armorSubClasses, { itemSubClass: item.itemSubClass });
      if (armorSubClass) {
        if (!_.includes(specInfo.class.armorTypes, armorSubClass.key)) {
          return 'wrong_armor_type';
        }
      }
    }
  }

  // check weapon/spec table
  if (_.includes(['main_hand', 'off_hand'], getItemSlotName(item))) {
    const weaponInfo = _.find(weaponClassDetails, { itemClass: item.itemClass, itemSubClass: item.itemSubClass });
    if (weaponInfo) {
      if (onlyDrop) {
        if (!_.includes(weaponInfo.specsCanDrop, specInfo.id)) {
          return 'item_does_not_drop';
        }
      } else if (!_.includes(weaponInfo.specsCanUse, specInfo.id)) {
        return 'spec_cannot_use';
      }
    }
  }

  // if there is no main stat at all, probably anyone can use the item.
  // This was initially an issue with Azshara's Font of Power but several SL items appear to use the asme
  // item categories that mess up the old analysis
  if (item.stats && !itemHasAnyMainStat(item)) {
    return null;
  }

  if (wrongMainStat) {
    return 'wrong_main_stat';
  }

  return null;
};

export const isItemUsable = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, false, false);
};

export const isItemIntended = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, false);
};

export const doesItemDrop = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, true);
};


export const getSocketBonuses = _.memoize(() => _.union(
  [1808], // default socket bonus ID to use when making faking sockets
  _.map(_.keys(GameData.BonusSockets), b => parseInt(b, 10)),
));

export const getCraftedStats = item => {
  // check bonus IDs first
  const bonusIds = getBonusIds(item);
  const craftedStatsBonuses = _.filter(_.map(bonusIds, bid => GameData.BonusCraftedStats[bid]));

  if (craftedStatsBonuses.length > 0) {
    return _.filter(_.flatten(craftedStatsBonuses));
  }

  // then check crafted_stats
  if (item.crafted_stats) {
    return _.map(_.filter(_.split(item.crafted_stats, '/')), cs => parseInt(cs, 10));
  }

  // otherwise there are no dynamic secondaries
  return null;
};


export const getEnhancements = _.memoize(() => _.union(
  getEnchants(),
  _.map(getGemInfo(), gem => ({
    ...gem,
    slot: 'socket',
  })),
));

export const findEnhancement = enhancementId => _.find(getEnhancements(), { id: enhancementId });

export const enhancementsForSlot = slot => _.filter(getEnhancements(), { slot });
export const enhancementsForSlotName = slotName => enhancementsForSlot(invertedSlotMap[slotName]);

export const getEnhancementsForSlotNameAndSpec = (slotName, specInfo) => {
  if (!slotName || !specInfo) {
    return [];
  }
  const classInfo = specInfo.class;
  let slotEnhancements;
  if (slotName === 'socket') {
    slotEnhancements = enhancementsForSlot('socket');
  } else {
    slotEnhancements = enhancementsForSlotName(slotName === 'off_hand' ? 'main_hand' : slotName);
  }
  return _.sortBy(
    _.filter(slotEnhancements, enchant => {
      // check for class requirement (e.g., DK weapon enchants)
      const requiredClasses = _.get(enchant, 'requirement.classes', null);
      if (requiredClasses) {
        // reject if no class info or if character is not the required class
        if (!classInfo || !_.includes(requiredClasses, classInfo.id)) {
          return false;
        }
      }

      return true;
    }),
    enhancement => _.get(enhancement, 'requirement.classes', Infinity),
  );
};

export const isPreferredEnhancement = (enhancement, specInfo) => {
  const preferredRole = _.includes(_.get(enhancement, 'preferred.roles'), specInfo.role);
  const preferredSpec = _.includes(_.get(enhancement, 'preferred.specs'), specInfo.id);

  const preferred = preferredRole || preferredSpec;

  // DKs only want DK weapon enchants
  if (enhancement.slot === MAIN_HAND_SLOT && _.includes([250, 251, 252], specInfo.id)) {
    return preferred && enhancement.group === 'death_knight';
  }

  return preferred;
};
export const getPreferredEnhancements = (slot, specInfo) => {
  return _.filter(
    getEnhancementsForSlotNameAndSpec(slot, specInfo),
    enhancement => isPreferredEnhancement(enhancement, specInfo),
  );
};

export const enchantsForItem = item => enhancementsForSlot(
  // get the internal "canonical" slot
  invertedSlotMap[getItemSlotName(item)],
);

export const isValidEnhancementForItem = (enhancement, item) => {
  // check for correct weapon enchant handedness
  const enchant = getEnchant(enhancement.enhancementId);
  if (enchant?.requirement?.hands) {
    const hands = getWeaponHands(item);
    if (hands !== enchant.requirement.hands) {
      return false;
    }
  }

  return true;
};

export const isEnchantable = item => {
  return item && _.includes(getEnchantableSlots(), item.inventoryType);
};

export const canRollSecondary = item => (
  item
  && item.chanceBonusList
  // compare secondary bonus IDs against chance bonus list, return true if any
  // bonus changes are presetn
  && _.intersection(_.map(secondaryBonuses, sb => sb.bonusId), item.chanceBonusList).length > 0
);

export const isTrinket = item => item && getItemSlotName(item) === 'trinket';

export const isRingOrTrinket = item => (
  item && _.includes(['finger', 'trinket'], getItemSlotName(item))
);

export const defaultSlot = (item) => {
  const slotName = getItemSlotName(item);
  if (isRingOrTrinket(item)) {
    return `${slotName}1`;
  }
  return slotName;
};

const CRAFT_STAT_1 = _.find(STATS, { enum: 'STAT_CRAFT_MOD_1' });
const CRAFT_STAT_2 = _.find(STATS, { enum: 'STAT_CRAFT_MOD_2' });
export const numCraftedStats = item => {
  if (!item) {
    return false;
  }

  const hasCraftStat1 = _.some(item.stats, { id: CRAFT_STAT_1.dataId });
  const hasCraftStat2 = _.some(item.stats, { id: CRAFT_STAT_2.dataId });
  return (+hasCraftStat1) + (+hasCraftStat2);
};
export const hasCraftedStats = item => {
  return numCraftedStats(item) > 0;
};
export const craftedStatsOrderMatters = item => {
  const alloc1 = _.find(item.stats, { id: CRAFT_STAT_1.dataId });
  const alloc2 = _.find(item.stats, { id: CRAFT_STAT_2.dataId });

  if (alloc1 && alloc2 && alloc1.alloc !== alloc2.alloc) {
    return true;
  }

  return false;
};

export const CRAFTED_STAT_COMBOS = _.sortBy(
  _.union(
    _.map(
      CRAFTED_STATS,
      stat => ({
        id: _.join([stat.id, stat.id], '/'),
        name: _.startCase(stat.name),
        value: stat.name,
        stats: [stat.id],
      }),
    ),
    _.map(
      Combinatorics.permutation(CRAFTED_STATS, 2).toArray(),
      combo => {
        return {
          id: _.join(_.map(combo, 'id'), '/'),
          name: _.join(_.map(combo, c => _.startCase(c.name)), '/'),
          value: _.join(_.map(combo, 'name'), ','),
          stats: _.map(combo, 'id'),
        };
      },
    ),
  ),
  'name',
);

// ----------- SOCKETS / GEMS --------------------------------------------

const getSocketTypeInfo = (socketInfo, type) => {
  return socketInfo ? socketInfo[type] : null;
};

export const getBonusIdForSocketNum = socketNum => {
  return parseInt(_.findKey(GameData.BonusSockets, num => num === socketNum), 10);
};

export const isUngatedSocketSlot = _.memoize(slot => _.includes(SLOT_SOCKETS_UNGATED, slot));

export const numStaticSockets = (item, type) => {
  if (!type) {
    throw new Error('numStaticSockets called without a socket type');
  }
  const socketInfo = getSocketTypeInfo(item?.socketInfo, type);
  if (socketInfo) {
    return socketInfo.staticSlots;
  }
  if (item?.socketInfo?.sockets) {
    return _.size(_.filter(item.socketInfo.sockets, { type }));
  }
  return 0;
};

export const numDynamicSockets = (item, type) => {
  if (!type) {
    throw new Error('numDynamicSockets called without a socket type');
  }
  if (_.includes(ROLLABLE_SOCKET_TYPES, type)) {
    if (item && item.bonus_id) {
      const bonusIds = _.map(_.filter(_.split(item.bonus_id, '/')), b => parseInt(b, 10));
      const socketBonusIds = _.intersection(getSocketBonuses(), bonusIds);
      if (_.size(socketBonusIds) > 0) {
        return GameData.BonusSockets[_.first(socketBonusIds)];
      }
    }
  }
  return 0;
};

export const canAddSocket = (item, type) => {
  if (!type) {
    throw new Error('canAddSocket called without a socket type');
  }

  if (!_.includes(ROLLABLE_SOCKET_TYPES, type)) {
    return false;
  }

  if (!item) {
    return false;
  }

  const socketInfo = getSocketTypeInfo(item.socketInfo, type);
  const slot = getItemSlotName(item);

  // already has a socket
  if (socketInfo?.staticSlots > 0 || socketInfo?.dynamicSlots > 0) {
    return socketInfo.dynamicSlots < SLOT_SOCKETS[slot];
  }

  const totalSockets = _.reduce(
    item.socketInfo,
    (acc, info) => acc + info.total,
    0,
  );
  if (totalSockets >= MAX_ITEM_SOCKETS) {
    return false;
  }

  // primordial gear can't roll prismatic sockets
  if (type === SOCKET_TYPES.PRISMATIC) {
    let hasOtherSocket = false;
    // full socket info data structure
    if (item.socketInfo && item.socketInfo[SOCKET_TYPES.PRIMORDIAL]?.total > 0) {
      hasOtherSocket = true;
    }
    // client-side data structure, shows in Droptimizer, probably should figure out how to get rid of this structure
    if (item.socketInfo?.sockets) {
      hasOtherSocket = _.find(item.socketInfo.sockets, { type: SOCKET_TYPES.PRIMORDIAL });
    }
    if (hasOtherSocket) {
      return false;
    }
  }

  // artifact
  if (item.quality === 6) {
    return false;
  }

  // legendary
  if (item.quality === 5) {
    // Old legendaries cannot be socketed, but shadowlands can be
    if (item.expansion < 8) {
      return false;
    }
  }

  // deny list of items that cannot be socketed even if they fulfill other constraints
  if (_.includes(ITEMS_NO_DYNAMIC_SOCKET, item.id)) {
    return false;
  }


  if (!SLOT_SOCKETS[slot]) {
    return false;
  }

  // Check for any bonus IDs that should deny a socket
  // Can be used to prevent socketing thigns like timewalking gear
  if (_.some(SOCKET_DISABLE_BONUSES, bonusId => hasBonusId(item, bonusId))) {
    return false;
  }

  return true;
};

export const numSockets = (item, type) => {
  if (!type) {
    throw new Error('numSockets called without a socket type');
  }

  const slot = getItemSlotName(item);

  const staticSockets = numStaticSockets(item, type);
  const dynamicSockets = canAddSocket(item, type) ? SLOT_SOCKETS[slot] : 0;

  if (staticSockets) {
    return staticSockets;
  }
  return dynamicSockets;
};

export const possibleSockets = item => {
  if (!item) {
    return [];
  }

  const sockets = [];

  if (item.socketInfo) {
    _.each(
      item.socketInfo,
      info => {
        for (let i = 0; i < info.total; i += 1) {
          sockets.push(info.type);
        }
      },
    );
  }
  if (_.size(sockets) < MAX_ITEM_SOCKETS && canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
    sockets.push(SOCKET_TYPES.PRISMATIC);
  }

  const slot = getItemSlotName(item);
  const socketsForSlot = SLOT_SOCKETS[slot];
  if (_.size(sockets) < socketsForSlot) {
    _.each(_.range(0, _.size(sockets) - socketsForSlot), () => sockets.push(SOCKET_TYPES.PRISMATIC));
  }

  return sockets;
};

export const itemSocketInfo = item => {
  const types = {};
  _.each(SOCKET_TYPES, type => {
    const itemSocketTypeInfo = item.socketInfo ? item.socketInfo[type] : null;
    // keep any static slots found from web item lookups
    // frontend item lookups don't have access to the full game data
    const staticSlots = itemSocketTypeInfo?.staticSlots || numStaticSockets(item, type);
    const dynamicSlots = numDynamicSockets(item, type);
    const gemDetails = _.filter(
      _.map(_.filter(_.split(item.gem_id, '/')), g => getGemDetails(parseInt(g, 10))),
      gem => gem && gem.type === type,
    );
    const gemIds = _.map(gemDetails, 'itemId');
    const hasUniqueGem = _.size(_.filter(gemIds, isGemUnique)) > 0;
    if (staticSlots || dynamicSlots || gemIds.length > 0) {
      types[type] = {
        type,
        staticSlots,
        dynamicSlots,
        filled: gemIds.length,
        total: _.max([staticSlots, dynamicSlots]),
        gems: gemDetails,
        gemIds,
        hasUnique: hasUniqueGem,
      };
    }
  });
  return types;
};

export const getItemSocketBonusId = item => {
  const bonusIds = item.bonus_id ? _.split(item.bonus_id, '/') : item.bonusLists;
  const socketBonusId = _.first(_.filter(
    bonusIds,
    bid => {
      return GameData.BonusSockets[bid];
    },
  ));
  return parseInt(socketBonusId, 10);
};

export const setItemNumSockets = (item, socketNum) => {
  let newItem = { ...item };
  const curBonusId = getItemSocketBonusId(item);

  if (curBonusId) {
    newItem = removeBonusIds(newItem, curBonusId);
  }

  newItem = addBonusIds(newItem, getBonusIdForSocketNum(socketNum));

  newItem.socketInfo = itemSocketInfo(newItem);

  return newItem;
};

export const addPrismaticSocket = (item) => {
  if (canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
    const newItem = { ...item };
    const bonusIds = _.map(_.filter(_.split(item.bonus_id, '/')), bid => parseInt(bid, 10));
    let newBonusIds = [...bonusIds];
    const curSocketBonusId = _.first(_.filter(bonusIds, bid => GameData.BonusSockets[bid]));
    if (!curSocketBonusId) {
      newBonusIds.push(_.first(getSocketBonuses()));
    } else {
      const newSocketNum = GameData.BonusSockets[curSocketBonusId] + 1;
      const newSocketBonusId = _.findKey(GameData.BonusSockets, num => num === newSocketNum);
      newBonusIds = _.without(newBonusIds, curSocketBonusId);
      newBonusIds.push(newSocketBonusId);
    }

    newItem.bonus_id = _.join(newBonusIds, '/');

    newItem.socketInfo = itemSocketInfo(newItem);

    return newItem;
  }

  return item;
};

export const addMaxPrismaticSockets = item => {
  const slot = getItemSlotName(item);

  let newItem = { ...item };

  _.each(_.range(1, SLOT_SOCKETS[slot]), () => {
    if (canAddSocket(newItem)) {
      newItem = addPrismaticSocket(newItem);
    }
  });

  return newItem;
};

export const removePrismaticSocket = item => {
  const curSocketsNum = numDynamicSockets(item, SOCKET_TYPES.PRISMATIC);
  const newSocketsNum = curSocketsNum - 1;

  let newItem = { ...item };

  const bonusIds = getBonusIds(newItem);

  const newBonusIds = _.reject(bonusIds, isSocketBonus);

  if (newSocketsNum > 0) {
    const newSocketBonusId = _.findKey(GameData.BonusSockets, n => n === newSocketsNum);
    newBonusIds.push(newSocketBonusId);
  }

  newItem = setBonusIds(newItem, newBonusIds);

  const newGems = _.take(_.split(newItem.gem_id, '/'), newSocketsNum);
  newItem.gem_id = _.join(newGems, '/');
  newItem.socketInfo = itemSocketInfo(newItem);

  return newItem;
};

export const removeAllPrismaticSockets = item => {
  let newItem = item;
  for (let i = 0; i < numDynamicSockets(item, SOCKET_TYPES.PRISMATIC); i += 1) {
    newItem = removePrismaticSocket(newItem);
  }
  return newItem;
};

export const getItemSlotsForSpec = (item, specId) => {
  const slots = [];
  const slotName = getItemSlotName(item);
  slots.push(slotName);

  // check for dual wielding
  const specInfo = getSpecInfoById(specId);
  const hands = getWeaponHands(item);
  const canDual1h = hands === '1h' && specInfo.dualWield;
  const canDual2h = hands === '2h' && specInfo.dualWield2h;
  if (canDual1h || canDual2h) {
    slots.push('off_hand');
  }

  return slots;
};


// WoW post on the original upgrade system for 10.1
// https://news.blizzard.com/en-us/world-of-warcraft/23944889/embers-of-neltharion-upgrade-system-overview

export const getItemUpgrades = (item, specId, character) => {
  const upgrades = [];

  if (item.upgrade) {
    const groupId = item.upgrade.group;
    const upgradeSet = GameData.BonusUpgradeSets[groupId];
    const currentUpgrade = _.find(upgradeSet, { level: item.upgrade.level });
    const specInfo = getSpecInfoById(specId);
    let totalCosts = _.isArray(item.upgradeCost) ? [...item.upgradeCost] : [];

    const slotName = getItemSlotName(item);

    // check MH/OH/1H/2H to determine right high watermark to use
    let redundancySlot;
    const hands = getWeaponHands(item);
    if (slotName === 'main_hand') {
      const map2h = {
        '2h': 'twohand',
        mh: 'mainhand',
        '1h': specInfo.dualWield ? 'onehandweaponsecond' : 'mainhand',
      };
      redundancySlot = map2h[hands];
    } else if (slotName === 'off_hand') {
      const mapOh = {
        '1h': 'onehandweaponsecond',
        oh: 'offhand',
      };

      redundancySlot = mapOh[hands];
    } else {
      redundancySlot = slotName;
    }


    let highWatermarks = _.find(character.slotHighWatermarks, { slot: discountSlotMap[redundancySlot] });

    if (_.includes(twoHandFallbackSlots, redundancySlot) && !_.includes(ignoreTwoHandFallbackSlotSpecs, specId)) {
      // from https://news.blizzard.com/en-us/world-of-warcraft/23944889/embers-of-neltharion-upgrade-system-overview
      // > And if you have a nice two-handed weapon, your highest will set the discount threshold for both hands no
      // > matter what style you use.
      const twoHandWatermarks = _.find(character.slotHighWatermarks, { slot: discountSlotMap.twohand });
      highWatermarks = {
        ...highWatermarks,
        character: _.max([highWatermarks?.character, twoHandWatermarks?.character]),
        account: _.max([highWatermarks?.account, twoHandWatermarks?.account]),
        twoHandMerge: true,
      };
    }
    // If the item is a 2h, check MH/OH combination high watermark
    if (redundancySlot === 'twohand') {
      const mainHandMark = _.find(character.slotHighWatermarks, { slot: discountSlotMap.mainhand });
      const oneHandMark = _.find(character.slotHighWatermarks, { slot: discountSlotMap.onehandweapon });
      const offHandMark = _.find(character.slotHighWatermarks, { slot: discountSlotMap.offhand });
      const comboMark = {
        character: _.min([
          _.max([mainHandMark?.character, oneHandMark?.character]),
          offHandMark?.character,
        ]),
        account: _.min([
          _.max([mainHandMark?.account, oneHandMark?.account]),
          offHandMark?.account,
        ]),
      };
      highWatermarks = {
        ...highWatermarks,
        character: _.max([highWatermarks?.character, comboMark?.character]),
        account: _.max([highWatermarks?.account, comboMark?.account]),
        mainOffHandMerge: true,
      };
    }

    for (let level = item.upgrade.level + 1; level <= item.upgrade.max; level += 1) {
      // find new item group entry
      const upgradeEntry = _.find(upgradeSet, { level });

      if (upgradeEntry && !upgradeEntry.disabled) {
        const discounts = _.filter(upgradeEntry.highWatermarkDiscounts, discount => {
          if (discount.accountWide) {
            return upgradeEntry.itemLevel <= highWatermarks?.account;
          }
          return upgradeEntry.itemLevel <= highWatermarks?.character;
        });

        if (upgradeEntry.costs) {
          // find the amount based on the inventory type mask
          const upgradeCost = _.find(upgradeEntry.costs, cost => {
            // TODO: Figure out if this is _actually_ correct
            // This feels like a hack, I suspect there's some source for these flags
            if (cost.flags === 1 && specInfo.mainStat !== 'int') {
              return false;
            }
            if (cost.flags === 2 && specInfo.mainStat === 'int') {
              return false;
            }
            // flags 4 and 8 are used on PVP gear, not sure what they indicate.
            // eslint-disable-next-line no-bitwise
            return cost.mask_inv_type & (2 ** item.inventoryType);
          });

          if (upgradeCost?.amounts) {
            // eslint-disable-next-line no-loop-func
            _.each(upgradeCost.amounts, amount => {
              const search = amount.currencyId ? { currencyId: amount.currencyId } : { itemId: amount.itemId };
              const currentCost = _.find(totalCosts, search);

              let scaleAmount = 1;
              const discountInfo = _.find(discounts, d => {
                if (d.type === 'currency') {
                  return d.id === amount.currencyId;
                }
                return d.id === amount.itemId;
              });
              if (discountInfo) {
                scaleAmount = discountInfo.scaling;
              } else if (upgradeEntry.itemLevel <= highWatermarks?.account) {
                // check for warband 33% discount if there's no other discount and then item level
                // is below the account high watermark
                const warbandDiscountAch = WARBAND_CREST_DISCOUNT_ACHIEVEMENT[amount.currencyId];
                if (_.includes(character.upgradeAchievements, warbandDiscountAch)) {
                  scaleAmount = 1 - WARBAND_CREST_DISCOUNT_AMOUNT;
                }
              }

              if (!currentCost) {
                const finalAmount = _.round(amount.amount * scaleAmount);
                if (finalAmount) {
                  totalCosts.push({
                    ...amount,
                    amount: finalAmount,
                  });
                }
              } else {
                const finalAmount = _.round(amount.amount * scaleAmount);
                if (finalAmount) {
                  totalCosts = [
                    ..._.reject(totalCosts, search),
                    {
                      ...currentCost,
                      amount: currentCost.amount + finalAmount,
                    },
                  ];
                }
              }
            });
          } else {
            // Probably only happens on PTR with poorly constructed test items?
            // eslint-disable-next-line no-console
            //console.error('No amount found for', item.name);
          }
        }

        let bonusIds = _.filter(_.split(item.bonus_id, '/'));

        // replace upgrade bonus ID
        bonusIds = _.without(bonusIds, currentUpgrade?.bonusId);
        bonusIds = _.concat(bonusIds, upgradeEntry.bonusId);

        // figure out correct bonus ID for item level
        const curLevelBonusId = _.find(bonusIds, bid => GameData.BonusLevels[bid]) || '1472';
        const newLevelOffset = upgradeEntry.itemLevel - item.itemLevel + GameData.BonusLevels[curLevelBonusId];
        const newLevelBonusId = getItemBonusForDelta(newLevelOffset);

        bonusIds = _.without(bonusIds, curLevelBonusId);
        bonusIds = _.concat(bonusIds, newLevelBonusId);

        const upgradeItem = {
          ...item,
          upgrade: upgradeEntry,
          itemLevel: upgradeEntry.itemLevel,
          bonus_id: _.join(bonusIds, '/'),
          upgraded: true,
          upgradeCost: _.orderBy(totalCosts, ['currencyId', 'itemId']),
          equipped: false,
        };
        upgrades.push(upgradeItem);
      }
    }
  }
  return _.orderBy(upgrades, ['itemLevel'], ['desc']);
};



export function getReagentSlot(reagentSlotId) {
  return GameData.Crafting.slots[reagentSlotId];
}

export function getReagent(reagentId) {
  return _.find(GameData.Crafting.reagents, { id: reagentId });
}

export function getSlotForReagent(reagentId) {
  const slots = _.filter(GameData.Crafting.slots, slotInfo => {
    return _.includes(slotInfo.itemIds, reagentId);
  });
  if (slots.length > 1) {
    throw new Error('Unexpected - more than 1 possible slot for a reagent');
  }
  return _.first(slots);
}

export function getEmbellishment(embellishmentId) {
  return getReagent(embellishmentId);
}

export function getPossibleEmbellishments(item) {
  if (!item?.profession) {
    return null;
  }

  const embellishSlots = _.intersection(
    _.map(item.profession.optionalCraftingSlots, 'id'),
    EMBELLISHED_REAGENT_SLOT_IDS,
  );
  if (!embellishSlots.length) {
    return null;
  }

  const embellishments = [];
  _.each(embellishSlots, embellishSlot => {
    const slotId = _.isObject(embellishSlot) ? embellishSlot.id : embellishSlot;
    const slot = getReagentSlot(slotId);
    _.each(slot.itemIds, id => {
      const embellishment = getEmbellishment(id);
      // If there are no bonus IDs, don't include
      // This mostly applies the Griftah item that remove embellishments during recrafting
      if (_.size(embellishment.craftingBonusIds) > 0) {
        embellishments.push(embellishment);
      }
    });
  });

  return embellishments;
}

export function replaceEmbellishment(item, embellishmentId) {
  const possibleEmbels = getPossibleEmbellishments(item);
  const embelBonusIds = _.flatten(_.map(possibleEmbels, 'craftingBonusIds'));

  // remove existing embellishment bonus IDs
  const bonusIds = _.map(_.filter(_.split(item.bonus_id, '/')), bid => parseInt(bid, 10));
  const cleanedBonusIds = _.reject(bonusIds, bid => _.includes(embelBonusIds, bid));

  if (!embellishmentId) {
    return {
      ...item,
      bonus_id: _.join(cleanedBonusIds, '/'),
      // MAYBE HACK - this _could_ blow away other item limits unrelated to embellishments
      itemLimit: undefined,
    };
  }

  const targetEmbel = _.find(possibleEmbels, { id: embellishmentId });
  if (!targetEmbel) {
    throw new Error(`replaceEmbellishment cannot find embellishment ${embellishmentId}`);
  }

  const newBonusIds = [
    ...cleanedBonusIds,
    ...targetEmbel.craftingBonusIds,
  ];

  return {
    ...item,
    bonus_id: _.join(newBonusIds, '/'),
    // MAYBE HACK - this _could_ blow away other item limits unrelated to embellishments
    itemLimit: targetEmbel.itemLimit,
  };
}

export const getPossibleItemConversions = (item, specId, itemConversionId) => {
  if (ITEM_CONVERSION_EXCLUDE_ITEM_IDS.has(item.id)) {
    return [];
  }

  const itemConversion = GameData.ItemConversions[itemConversionId];

  // Can happen if there's a data version mismatch between frontend and web
  if (!itemConversion) {
    return [];
  }

  const specInfo = getSpecInfoById(specId);

  const possibleItems = _.filter(itemConversion.items, it => {
    // can't convert an item to itself
    if (it.id === item.id) {
      return false;
    }
    if (it.allowableClasses && !_.includes(it.allowableClasses, specInfo.class.id)) {
      return false;
    }
    if (it.specs && !_.includes(it.specs, specId)) {
      return false;
    }
    if (getItemSlotName(item) !== getItemSlotName(it)) {
      return false;
    }
    return true;
  });
  return _.map(possibleItems, it => {
    let newItem = {
      ...it,
      // copy over any item upgrade info
      upgrade: item.upgrade,
      upgradeCost: item.upgradeCost,
      upgraded: item.upgraded,
      source: item.source,
      tags: item.tags,
    };

    // copy over relevant bonus IDs (tertiary, upgrade level, socket)
    const keepBonusIds = _.filter(getBonusIds(item), bonusId => {
      if (GameData.BonusSockets[bonusId]) {
        return true;
      }
      if (_.includes(tertiaryBonusIds, bonusId)) {
        return true;
      }
      if (getUpgradeBonusIds().has(bonusId)) {
        return true;
      }
      return false;
    });

    newItem = addBonusIds(newItem, keepBonusIds);

    newItem = updateItemLevel(newItem, item.itemLevel);

    return newItem;
  });
};


export const getStatRecrafts = item => {
  if (!item) {
    return null;
  }

  const recrafts = [];

  const statCraftingSlot = _.find(
    item.profession?.optionalCraftingSlots,
    slot => _.includes(RECRAFT_STAT_SLOTS, slot.id || slot),
  );

  const statCraftingSlotId = statCraftingSlot?.id || statCraftingSlot;

  if (statCraftingSlotId) {
    const slot = GameData.Crafting.slots[statCraftingSlotId];
    const rawPotentialBonusIds = [];
    _.each(slot.itemIds, reagentId => {
      const reagent = _.find(GameData.Crafting.reagents, { id: reagentId });
      if (_.size(reagent.craftingBonusIds)) {
        rawPotentialBonusIds.push(reagent.craftingBonusIds);
      }
    });
    const potentialBonusIds = _.uniqBy(rawPotentialBonusIds, bids => _.join(bids, '/'));
    const flatPotentialBonusIds = _.flatten(potentialBonusIds);

    const currentBonusIds = getBonusIds(item);
    const cleanBonusIds = _.reject(currentBonusIds, bid => _.includes(flatPotentialBonusIds, bid));

    _.each(potentialBonusIds, statBonusIds => {
      const itemHasThisBonus = _.size(_.intersection(currentBonusIds, statBonusIds)) > 0;
      if (!itemHasThisBonus) {
        let newItem = setBonusIds(item, cleanBonusIds);
        newItem = addBonusIds(newItem, statBonusIds);

        // look up the name of the secondary stats from this bonus ID.
        // storing it as random property on the item isn't great but I'm lazy.
        const craftedStats = GameData.BonusCraftedStats[_.first(statBonusIds)];
        const statNames = _.join(
          _.map(craftedStats, stat => _.find(STATS, { dataId: stat })?.shortName),
          ' / ',
        );
        newItem.statNames = statNames;

        recrafts.push(newItem);
      }
    });
  }


  return recrafts;
};

export const getBonusVariations = (item, skipCurrent = true) => {
  if (!item) {
    return null;
  }

  const bonusVariations = _.find(ITEM_BONUS_VARIATIONS, { itemId: item.id })?.variations;

  if (!bonusVariations) {
    return null;
  }

  const variations = [];
  const allBonusIds = _.filter(_.map(bonusVariations, 'bonusId'));
  const itemHasBonusId = _.some(allBonusIds, bonusId => hasBonusId(item, bonusId));

  _.each(bonusVariations, ({ bonusId, name }) => {
    if (skipCurrent) {
      if (bonusId) {
        if (hasBonusId(item, bonusId)) {
          // item is already this variation
          return;
        }
      } else if (!itemHasBonusId) {
        // item has none of the bonus IDs and this variation is the null ID case
        return;
      }
    }
    // remove any
    let variation = removeBonusIds(item, allBonusIds);
    if (bonusId) {
      variation = addBonusIds(variation, bonusId);
    }
    variation.bonusVariationName = name;

    variations.push(variation);
  });

  return variations;
};

export const getCraftingAugments = _.memoize(() => {
  const augments = [];
  _.each(GameData.Crafting.slots, (slot, slotIdStr) => {
    const slotId = parseInt(slotIdStr, 10);
    if (_.includes(CRAFTING_LEVEL_AUGMENT_SLOTS, slotId)) {
      const slotInfo = getReagentSlot(slotId);
      if (slotInfo) {
        _.each(slotInfo.itemIds, reagentId => {
          const reagent = getReagent(reagentId);
          augments.push(reagent);
        });
      }
    }
  });

  return augments;
});


export const getCraftingUpgradeLevels = _.memoize(() => {
  const upgradeLevels = [];
  _.each(CRAFTING_LEVEL_UPGRADE_SLOTS, slotId => {
    const slotInfo = getReagentSlot(slotId);
    _.each(slotInfo.itemIds, reagentId => {
      const reagent = getReagent(reagentId);
      const baseLevelBonuses = _.filter(_.map(reagent.craftingBonusIds, bonusId => {
        const baseLevelInfo = GameData.BonusBaseLevels[bonusId];
        if (baseLevelInfo) {
          return {
            bonusId,
            baseLevel: baseLevelInfo.base_level,
            baseLevelPriority: baseLevelInfo.base_level_priority,
          };
        }
        return null;
      }));

      // find augment items for the base reagent
      // look at craftingPrereqs for base item and then find augments with the same craftingPrereq
      const possibleAugments = [];
      if (reagent.craftingPrereqs) {
        const basePrereqItemIds = _.map(reagent.craftingPrereqs, 'itemId');
        const augments = getCraftingAugments();
        _.each(augments, augment => {
          const augmentPrereqItemIds = _.map(augment.craftingPrereqs, 'itemId');
          if (_.size(augmentPrereqItemIds) && _.size(_.intersection(basePrereqItemIds, augmentPrereqItemIds))) {
            possibleAugments.push(augment);
          }
        });
      }

      // This would be weird
      if (baseLevelBonuses.length > 1) {
        throw new Error('Found more than 1 base level bonus ID for a crafted item in getCraftingLevelUpgrades!');
      }
      if (baseLevelBonuses.length === 1) {
        const { baseLevel } = _.first(baseLevelBonuses);
        _.each(professionCraftingQualities, pcq => {
          upgradeLevels.push({
            item: reagent,
            slotId,
            level: baseLevel + pcq.levelOffset,
            craftingQuality: pcq.tier,
            bonusIds: [
              ...reagent.craftingBonusIds,
              ..._.map(reagent.craftingPrereqs, 'bonusId'),
            ],
          });

          _.each(possibleAugments, augment => {
            const augmentLevelOffset = getItemLevelAdjust(augment.craftingBonusIds);
            upgradeLevels.push({
              item: reagent,
              augmentItem: augment,
              slotId,
              level: baseLevel + pcq.levelOffset + augmentLevelOffset,
              craftingQuality: pcq.tier,
              bonusIds: [
                ...reagent.craftingBonusIds,
                ..._.map(reagent.craftingPrereqs, 'bonusId'),
                ..._.map(augment.craftingBonusIds),
              ],
            });
          });
        });
      }
    });
  });

  const sorted = _.orderBy(upgradeLevels, ['level', 'craftingQuality'], ['asc', 'asc']);

  return sorted;
});

export const getCraftingUpgradesForLevel = level => {
  return _.filter(getCraftingUpgradeLevels(), { level });
};

export const getCraftingUpgradesForItemAndLevel = (item, level) => {
  return _.filter(
    getCraftingUpgradesForLevel(level),
    upgrade => _.find(item.profession.optionalCraftingSlots, { id: upgrade.slotId }),
  );
};

export const getCraftingSlotsForReagent = (itemId) => {
  return _.filter(GameData.Crafting.slots, slotInfo => _.includes(slotInfo.itemIds, itemId));
};

export const getCraftingUpgradeCosts = (item, itemLevel) => {
  if (!item) {
    return null;
  }

  if (!item.profession) {
    return null;
  }

  // filter out upgrade items that don't fit in this item
  const professionUpgrades = _.orderBy(
    getCraftingUpgradesForItemAndLevel(item, itemLevel),
    ['craftingQuality'],
    ['desc'],
  );

  const upgradeCosts = _.filter(_.map(professionUpgrades, professionUpgrade => {
    let upgradeCost = [...(item.upgradeCost || [])];

    if (professionUpgrade) {
      const slotId = professionUpgrade.slotId;

      const reagent = getReagent(professionUpgrade.item.id);
      const altReagent = CRAFTING_CURRENCY_ALTERNATE[reagent.id];

      const itemSlot = _.find(item.profession.optionalCraftingSlots, { id: slotId });

      const bonusIdIntersection = _.intersection(reagent.craftingBonusIds, getBonusIds(item));
      const hasBonusIds = _.size(bonusIdIntersection) === _.size(reagent.craftingBonusIds);

      if (hasBonusIds) {
        // item already has bonus IDs for this upgrade so bail out
        return null;
      }

      upgradeCost = [{
        itemId: professionUpgrade.item.id,
        amount: itemSlot.count || 1,
        bonusId: professionUpgrade.bonusIds,

        name: reagent.name,
        icon: reagent.icon,

        alternateItemId: altReagent?.id,
        alternateItemAmount: altReagent?.amount,
        alternateItemName: altReagent?.name,
        alternateItemIcon: altReagent?.icon,
      }];

      if (professionUpgrade.augmentItem) {
        const augReagent = professionUpgrade.augmentItem;
        const augAltReagent = CRAFTING_CURRENCY_ALTERNATE[augReagent.id];
        upgradeCost.push({
          itemId: augReagent.id,
          amount: 1,
          bonusId: augReagent.craftingBonusIds,
          augment: true,

          name: augReagent.name,
          icon: augReagent.icon,

          alternateItemId: augAltReagent?.id,
          alternateItemAmount: augAltReagent?.amount,
          alternateItemName: augAltReagent?.name,
          alternateItemIcon: augAltReagent?.icon,
        });
      }

      _.each(professionUpgrade.item.craftingPrereqs, prereq => {
        const prereqReagent = getReagent(prereq.itemId);
        const prereqSlot = getSlotForReagent(prereq.itemId);
        const prereqItemSlot = _.find(item.profession.optionalCraftingSlots, { id: prereqSlot.reagentSlotId });
        const altPrereq = CRAFTING_CURRENCY_ALTERNATE[prereq.itemId];

        const prereqBonusIdIntersection = _.intersection(prereqReagent.craftingBonusIds, getBonusIds(item));
        const hasPrereqBonusIds = _.size(prereqBonusIdIntersection) === _.size(prereqReagent.craftingBonusIds);

        if (hasPrereqBonusIds) {
          // skip this prereq reagent since it must already be applied
          return;
        }

        upgradeCost.push({
          itemId: prereq.itemId,
          amount: prereqItemSlot.count || 1,
          bonusId: prereq.bonusId,

          name: prereqReagent.name,
          icon: prereqReagent.icon,

          alternateItemId: altPrereq?.id,
          alternateItemAmount: altPrereq?.amount,
          alternateItemName: altPrereq?.name,
          alternateItemIcon: altPrereq?.icon,
        });
      });
    }

    return upgradeCost;
  }));

  return _.first(upgradeCosts);
};

export const getItemCraftingAugments = item => {
  let current;
  const possible = [];

  const itemBonusIds = getBonusIds(item);

  _.each(item.profession.optionalCraftingSlots, slot => {
    const slotId = slot.id || slot;
    if (_.includes(CRAFTING_LEVEL_AUGMENT_SLOTS, slotId)) {
      const slotInfo = getReagentSlot(slotId);
      if (slotInfo) {
        _.each(slotInfo.itemIds, reagentId => {
          const reagent = getReagent(reagentId);
          if (_.intersection(itemBonusIds, reagent.craftingBonusIds).length === reagent.craftingBonusIds.length) {
            current = reagent;
          }
          possible.push(reagent);
        });
      }
    }
  });

  return {
    current,
    possible,
  };
};

export const getCraftingAugmentUpgrades = (item) => {
  if (!item) {
    return null;
  }

  if (!item.profession) {
    return null;
  }

  const upgrades = [];

  const {
    possible: possibleAugmentItems,
    current: currentItemAugmentUpgrade,
  } = getItemCraftingAugments(item);

  let currentOffset = 0;
  if (currentItemAugmentUpgrade) {
    currentOffset = getItemLevelAdjust(currentItemAugmentUpgrade.craftingBonusIds);
  }

  _.each(possibleAugmentItems, augmentReagent => {
    let available = true;
    const augmentOffset = getItemLevelAdjust(augmentReagent.craftingBonusIds);
    if (currentItemAugmentUpgrade) {
      available = augmentOffset > currentOffset;
    }
    if (available) {
      const altReagent = CRAFTING_CURRENCY_ALTERNATE[augmentReagent.id];
      const upgradeCost = [{
        itemId: augmentReagent.id,
        amount: 1,
        bonusId: augmentReagent.craftingBonusIds,

        name: augmentReagent.name,
        icon: augmentReagent.icon,

        alternateItemId: altReagent?.id,
        alternateItemAmount: altReagent?.amount,
        alternateItemName: altReagent?.name,
        alternateItemIcon: altReagent?.icon,
      }];
      let newItem = { ...item };
      newItem = addBonusIds(newItem, augmentReagent.craftingBonusIds);
      // HACK: Manually adjusting item level is fucked but we need to do it because updateItemLevel doesn't
      // have special handling for multiple item offset bonuses
      newItem.itemLevel += augmentOffset;
      if (currentItemAugmentUpgrade) {
        newItem.itemLevel -= currentOffset;
        newItem = removeBonusIds(newItem, currentItemAugmentUpgrade.craftingBonusIds);
        newItem.upgradeCost = _.reject(newItem.upgradeCost, { itemId: currentItemAugmentUpgrade.id });
      }
      newItem.upgradeCost = [...(newItem.upgradeCost || []), ...upgradeCost];
      upgrades.push(newItem);
    }
  });

  return _.orderBy(upgrades, ['itemLevel'], ['desc']);
};

export const getCraftingLevelUpgrades = (item) => {
  if (!item) {
    return null;
  }

  if (!item.profession) {
    return null;
  }

  const upgrades = [];

  const itemBonusIds = getBonusIds(item);
  const itemLevelAdjust = getItemLevelAdjust(itemBonusIds);

  // Will we be back in 10.2 with multi-step requirements?

  _.each(item.profession.optionalCraftingSlots, slot => {
    const slotId = slot.id || slot;
    if (_.includes(CRAFTING_LEVEL_UPGRADE_SLOTS, slotId)) {
      const slotInfo = getReagentSlot(slotId);
      _.each(slotInfo.itemIds, reagentId => {
        const reagent = getReagent(reagentId);
        const baseLevelBonuses = _.filter(_.map(reagent.craftingBonusIds, bonusId => {
          const baseLevelInfo = GameData.BonusBaseLevels[bonusId];
          if (baseLevelInfo) {
            return {
              bonusId,
              baseLevel: baseLevelInfo.base_level,
              baseLevelPriority: baseLevelInfo.base_level_priority,
            };
          }
          return null;
        }));


        // This would be weird
        if (baseLevelBonuses.length > 1) {
          throw new Error('Found more than 1 base level bonus ID for a crafted item in getCraftingLevelUpgrades!');
        }
        if (baseLevelBonuses.length === 1) {
          const { baseLevel, baseLevelPriority, bonusId } = _.first(baseLevelBonuses);
          const itemLevel = baseLevel + itemLevelAdjust;

          if (itemLevel > item.itemLevel) {
            let newBonusIds = getBonusIds(item);

            // remove bonus IDs with the same or lower base level priority.
            // This should clear out base levels from previous/older crafting steps.
            // This used to just clear the same priority but it might just need to clear all of them?
            // Current issue seen in 10.2 is that 10.1 Aspect Crest upgrade has a lower priority but also lower level.
            // Maybe just remove any base level lower than the target?
            newBonusIds = _.reject(
              newBonusIds,
              bid => baseLevelPriority >= GameData.BonusBaseLevels[bid]?.base_level_priority,
            );
            newBonusIds.push(bonusId);
            let newItem = setBonusIds(item, newBonusIds);
            newItem.itemLevel = itemLevel;

            // rest of this function does some stuff that getCraftingUpgradeCosts also sort of does.
            // TODO: Probably worth trying to clean up at some point
            // ALSO IMPORTANT - pass in the old item with the old item level
            // TODO: This should just make the whole new item to avoid this shit show
            const upgradeCost = getCraftingUpgradeCosts(item, itemLevel);

            if (!upgradeCost) {
              return;
            }

            // Make sure we inherit any previous costs
            // TODO: Maybe need to do a better merge here? I don't think we can currently have
            // repeated materials but if that does happen, it would be weird to see something like
            // 1 Spark + 1 Spark + 1 Spark
            // intead of
            // 3 Sparks
            // TODO UPDATE May 2025 - just unique the item/currency ID. This is a hack
            // This will hurt later. This always hurts
            const prevUpgradeCosts = item.upgradeCost || [];
            newItem.upgradeCost = _.uniqBy([...prevUpgradeCosts, ...upgradeCost], c => c.itemId || c.currencyId);

            // If an upgrade requires a Spark, it's probably a season transition and
            // any sockets won't be retained.
            if (getItemSlotName(item) !== 'neck' && _.includes(CRAFTING_SLOT_REMOVE_SOCKETS, slotId)) {
              newItem = removePrismaticSocket(newItem);
            }

            // god this is busted
            // should probably build a more general recursive solution?
            if (reagent.craftingPrereqs) {
              _.each(reagent.craftingPrereqs, craftingPrereq => {
                if (!hasBonusId(newItem, craftingPrereq.bonusId)) {
                  const prereqSlot = getSlotForReagent(craftingPrereq.itemId);
                  const prereqSlotId = prereqSlot.reagentSlotId;

                  let newItemWithPrereqCost = addBonusIds(newItem, craftingPrereq.bonusId);

                  // need to do this check like above to capture items with multiple item level reagents
                  if (getItemSlotName(item) !== 'neck' && _.includes(CRAFTING_SLOT_REMOVE_SOCKETS, prereqSlotId)) {
                    newItemWithPrereqCost = removePrismaticSocket(newItemWithPrereqCost);
                  }

                  upgrades.push(newItemWithPrereqCost);
                } else {
                  upgrades.push(newItem);
                }
              });
            } else {
              upgrades.push(newItem);
            }
          }
        }
      });
    }
  });

  return _.orderBy(upgrades, ['itemLevel'], ['desc']);
};

export const getItemConversion = (item, specId) => {
  if (!specId || !item) {
    return null;
  }

  const bonusIds = getBonusIds(item);
  const itemConversion = _.find(GameData.ItemConversions, conversion => {
    return _.size(_.intersection(bonusIds, conversion.bonusIds)) > 0;
  });
  if (itemConversion) {
    const possibleItems = getPossibleItemConversions(item, specId, itemConversion.id);
    if (_.size(possibleItems) === 0) {
      return null;
    }
    if (_.size(possibleItems) > 1) {
      throw new Error(
        `Found multiple possible item conversions. item: ${item.name} ${item.id} conversion ${itemConversion.id}`,
      );
    }
    const newItem = _.first(possibleItems);
    return newItem;
  }

  return null;
};

const getCraftingQualityUpgrades = (item) => {
  const upgrades = [];
  if (item.crafting_quality) {
    const curTier = parseInt(item.crafting_quality, 10);
    const curLevelOffset = _.find(professionCraftingQualities, { tier: curTier })?.levelOffset || 0;

    const start = curTier + 1;
    const end = _.max(_.map(professionCraftingQualities, 'tier')) + 1;
    _.each(_.range(start, end), tier => {
      const newLevelOffset = _.find(professionCraftingQualities, { tier }).levelOffset;
      const diff = newLevelOffset - curLevelOffset;
      const newItem = updateItemLevel(item, item.itemLevel + diff);

      newItem.crafting_quality = tier;

      upgrades.push(newItem);
    });
  }
  return upgrades;
};

// HACK: Do a fake stat transformation to make Best-in-Slots show up correctly in Top Gear
// This does not correctly change the stat allocation for StrAgi -> Int, it only makes the change
// to make warnings disappear in Top Gear
const bestInSlotsStatTransform = item => {
  return _.mapValues(item, (val, key) => {
    if (key === 'stats') {
      return _.map(val, stat => {
        if (stat.id === 72) {
          return {
            id: 5,
            alloc: stat.alloc,
          };
        }
        if (stat.id === 5) {
          return {
            id: 72,
            alloc: stat.alloc,
          };
        }
        return stat;
      });
    }
    return val;
  });
};

const ITEM_TRANSFORMATIONS = [{
  // best-in-slots agi/str -> int
  sourceId: 232526,
  destId: 232805,
  func: bestInSlotsStatTransform,
}, {
  // best-in-slots int -> agi/str
  sourceId: 232805,
  destId: 232526,
  func: bestInSlotsStatTransform,
}];

const getItemTransformations = (item) => {
  const transforms = _.filter(ITEM_TRANSFORMATIONS, { sourceId: item.id });
  if (_.size(transforms)) {
    return _.map(transforms, ({ destId, func }) => {
      let newItem = { ...item };
      if (func) {
        newItem = func(newItem);
      }
      return {
        ...newItem,
        id: destId,
      };
    });
  }

  return null;
};

const TITAN_DISC_BELT_ITEMS = [
  242664,
  245964,
  245965,
  245966,
];

export const TITAN_DISC_BELT_VERSIONS = [{
  id: 1236109,
  name: 'Charged Bolts',
  spellModifier: 1241244,
}, {
  id: 1236122,
  name: 'Cauterizing Bolts',
  spellModifier: 1241245,
}, {
  id: 1236272,
  name: 'Critical Chain',
  spellModifier: 1241246,
}, {
  id: 1236273,
  name: 'Spark Burst',
  spellModifier: 1241251,
}, {
  id: 1236275,
  name: 'Static Charge',
  spellModifier: 1241250,
}, {
  id: 1236277,
  name: 'Charged Touch',
  spellModifier: 1241242,
}, {
  id: 1236278,
  name: 'Energy Shield',
  spellModifier: 1241241,
}, {
  id: 1236279,
  name: 'Charged Crystal',
  spellModifier: 1241240,
}, {
  id: 1236961,
  name: 'Electric Current',
  spellModifier: 1241243,
}];

export const isTitanDiscBelt = item => {
  if (!item) {
    return false;
  }

  return _.includes(TITAN_DISC_BELT_ITEMS, parseInt(item.id, 10));
};

export const getTitanDiscInfo = item => {
  if (!item) {
    return null;
  }
  return _.find(TITAN_DISC_BELT_VERSIONS, { id: parseInt(item.titan_disc_id, 10) });
};

export const getTitanDiscBeltTransforms = (item) => {
  if (!item) {
    return null;
  }

  const versions = _.reject(TITAN_DISC_BELT_VERSIONS, { id: parseInt(item.titan_disc_id, 10) });

  return _.map(versions, version => {
    return {
      ...item,
      titan_disc_id: version.id,
      discVariation: version.name,
    };
  });
};

const CUSTOM_ITEM_LEVELS = [{
  // Properties:
  // itemId: int
  // itemFilterFn: function
  // itemLevels: array(int)
  itemFilterFn: isTitanDiscBelt,
  itemLevels: [
    691,
    694,
    697,
    701,
  ],
}];

const getCustomItemLevels = item => {
  if (!item) {
    return [];
  }
  const variations = [];
  _.each(CUSTOM_ITEM_LEVELS, ({ itemId, itemFilterFn, itemLevels }) => {
    let match = false;
    if (itemId && item.id === itemId) {
      match = true;
    } else if (itemFilterFn && itemFilterFn(item)) {
      match = true;
    }
    if (match) {
      _.each(itemLevels, itemLevel => {
        if (itemLevel > item.itemLevel) {
          variations.push(updateItemLevel(item, itemLevel));
        }
      });
    }
  });

  return _.orderBy(variations, ['itemLevel'], ['desc']);
};

export const getItemMutations = (item, character, upgradeCurrencies = {}) => {
  const mutations = [];

  const specInfo = getCharacterSpecInfo(character);

  const slot = slotMap[item.inventoryType];

  const currencyWarning = 'Use the Item Upgrade Currency panel to add currency';

  // Item Transforms
  // When an item can be swapped between two different versions that have different IDs
  const itemTransformations = getItemTransformations(item);
  _.each(itemTransformations, transformedItem => {
    mutations.push({
      key: `transform-${item.id}-${transformedItem.id}`,
      name: 'Transform',
      item: addTag(transformedItem, 'item-transform'),
      type: 'item-transform',
    });
  });

  // item upgrades
  const upgradeItems = getItemUpgrades(item, specInfo.id, character);
  _.each(upgradeItems, upgradeItem => {
    const upgrade = upgradeItem.upgrade;
    const affordable = _.every(upgradeItem.upgradeCost, cost => {
      return upgradeCurrencies[cost.currencyId || cost.itemId] >= cost.amount;
    });
    mutations.push({
      key: `upgrade-${upgrade.group}-${upgrade.level}`,
      name: `Upgrade to ${upgradeItem.itemLevel}`,
      item: addTag(upgradeItem, 'item-upgrade'),
      type: 'item-upgrade',
      disabled: !affordable,
      disabledReason: 'Cannot afford',
      disabledAction: currencyWarning,
      affordable,
    });
  });

  // crafting - item level upgrades
  const craftingLevelUpgrades = getCraftingLevelUpgrades(item);
  _.each(craftingLevelUpgrades, upgradeItem => {
    const affordable = _.every(upgradeItem.upgradeCost, cost => {
      const primaryAmount = upgradeCurrencies[cost.itemId];
      if (primaryAmount >= cost.amount) {
        return true;
      }
      const alternateAmount = upgradeCurrencies[cost.alternateItemId];
      if (alternateAmount && alternateAmount >= cost.alternateItemAmount) {
        return true;
      }

      return false;
    });

    mutations.push({
      key: _.join(['crafting', upgradeItem.itemLevel, ..._.map(upgradeItem.upgradeCost, 'itemId')], '-'),
      name: `Upgrade to ${upgradeItem.itemLevel}`,
      item: addTag(upgradeItem, 'item-upgrade'),
      type: 'item-upgrade',
      disabled: !affordable,
      disabledReason: 'Cannot afford',
      disabledAction: currencyWarning,
      affordable,
    });
  });

  // crafting - item level augments (smaller bumps to crest base levels)
  const craftingAugmentUpgrades = getCraftingAugmentUpgrades(item);
  _.each(craftingAugmentUpgrades, upgradeItem => {
    const affordable = _.every(upgradeItem.upgradeCost, cost => {
      const primaryAmount = upgradeCurrencies[cost.itemId];
      if (primaryAmount >= cost.amount) {
        return true;
      }
      const alternateAmount = upgradeCurrencies[cost.alternateItemId];
      if (alternateAmount && alternateAmount >= cost.alternateItemAmount) {
        return true;
      }

      return false;
    });

    mutations.push({
      key: _.join(['augment', upgradeItem.itemLevel, ..._.map(upgradeItem.upgradeCost, 'itemId')], '-'),
      name: `Augment to ${upgradeItem.itemLevel}`,
      item: addTag(upgradeItem, 'item-upgrade'),
      type: 'item-upgrade',
      disabled: !affordable,
      disabledReason: 'Cannot afford',
      disabledAction: currencyWarning,
      affordable,
    });
  });

  const customItemLevels = getCustomItemLevels(item);
  _.each(customItemLevels, customItemLevel => {
    mutations.push({
      key: _.join(['custom-item-level', customItemLevel.itemLevel], '-'),
      name: `Upgrade to ${customItemLevel.itemLevel}`,
      item: addTag(customItemLevel, 'custom-item-level'),
      type: 'custom-item-level',
    });
  });


  const craftingQualityUpgrades = getCraftingQualityUpgrades(item);
  _.each(craftingQualityUpgrades, upgradeItem => {
    mutations.push({
      key: _.join(['craftingQuality', upgradeItem.itemLevel], '-'),
      name: `Recraft to ${upgradeItem.itemLevel}`,
      item: addTag(upgradeItem, 'recraft'),
      type: 'crafting-quality',
    });
  });


  // embellished
  const embellishments = getPossibleEmbellishments(item);
  if (embellishments) {
    const dedupedEmbels = _.map(_.groupBy(embellishments, 'name'), _.last);
    _.each(dedupedEmbels, embellishment => {
      // remove any existing embellishment bonus IDs
      mutations.push({
        name: `Embellishment: ${embellishment.name}`,
        item: addTag(replaceEmbellishment(item, embellishment.id), 'recraft'),
        type: 'embellishment',
      });
    });
    if (item.itemLimit) {
      mutations.push({
        name: 'Remove Embellishment',
        item: addTag(replaceEmbellishment(item, null), 'recraft'),
        type: 'embellishment',
      });
    }
  }

  // conversions (catalyst)
  const itemConversion = getItemConversion(item, specInfo.id);
  if (itemConversion) {
    mutations.push({
      name: 'Convert to Catalyst Item',
      item: addTag(itemConversion, 'catalyst'),
      type: 'catalyst',
    });
  }

  // recrafting stats
  const statRecrafts = getStatRecrafts(item);
  if (_.size(statRecrafts)) {
    _.each(statRecrafts, recraftItem => {
      mutations.push({
        name: `Recraft to ${recraftItem.statNames}`,
        item: addTag(recraftItem, 'recraft'),
        type: 'recraft',
      });
    });
  }

  // items with bonus ID variations
  const bonusVariations = getBonusVariations(item);
  if (_.size(bonusVariations)) {
    _.each(bonusVariations, variation => {
      mutations.push({
        name: `Change to ${variation.bonusVariationName}`,
        item: addTag(variation, 'bonus-variation'),
        type: 'bonus-variation',
      });
    });
  }

  // 11.1.7 DISC Belt
  if (isTitanDiscBelt(item)) {
    const discVariations = getTitanDiscBeltTransforms(item);
    _.each(discVariations, variation => {
      mutations.push({
        name: `Change to ${variation.discVariation}`,
        item: addTag(variation, 'disc-belt-variation'),
        type: 'disc-belt-variation',
      });
    });
  }


  const prismaticInfo = item.socketInfo[SOCKET_TYPES.PRISMATIC];

  // add socket(s)
  if (canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
    if (SLOT_SOCKETS[slot] > 1) {
      // quick add all sockets
      const curSockets = prismaticInfo?.total || 0;
      if (curSockets < SLOT_SOCKETS[slot]) {
        let maxSocketItem = { ...item };
        _.each(_.range(curSockets, SLOT_SOCKETS[slot]), () => {
          maxSocketItem = addTag(addPrismaticSocket(maxSocketItem), 'add-socket');
        });
        mutations.push({
          name: 'Add max sockets',
          item: maxSocketItem,
          type: 'add-socket',
        });
      }
    }
    mutations.push({
      name: 'Add socket',
      item: addTag(addPrismaticSocket(item), 'add-socket'),
      type: 'add-socket',
    });

    if (!isUngatedSocketSlot(slot)) {
      if (item.source !== 'weekly_reward') {
        mutations.push({
          name: 'Add socket (Great Vault)',
          item: addTag(addTag(addPrismaticSocket(item), 'weekly_reward'), 'add-gv-socket'),
          type: 'add-socket',
        });
      }
    }
  }

  if (prismaticInfo && prismaticInfo.dynamicSlots - prismaticInfo.staticSlots > 0) {
    mutations.push({
      name: 'Remove socket',
      item: addTag(removePrismaticSocket(item), 'remove-socket'),
      type: 'remove-socket',
    });
  }

  // remove gem
  if (item.gem_id) {
    mutations.push({
      name: 'Remove gem(s)',
      item: addTag({
        ...item,
        gem_id: undefined,
      }, 'remove-gem'),
      type: 'remove-gem',
    });
  }

  // remove enchant
  if (item.enchant_id) {
    mutations.push({
      name: 'Remove enchant',
      item: addTag({
        ...item,
        enchant_id: undefined,
      }, 'remove-enchant'),
      type: 'remove-enchant',
    });
  }

  return _.map(mutations, it => ({
    ...it,
    equipped: false,
  }));
};

export const isHighSecondaryProcTrinket = itemId => {
  return _.includes(HIGH_SECONDARY_PROC_TRINKETS, itemId);
};

export const sumGearUpgradeCost = items => {
  const upgradeCurrencyUsed = {};
  _.each(items, item => {
    if (item.upgradeCost) {
      _.each(item.upgradeCost, cost => {
        const costId = cost.currencyId || cost.itemId;
        if (!upgradeCurrencyUsed[costId]) {
          upgradeCurrencyUsed[costId] = {
            ...cost,
            amount: 0,
          };
        }
        upgradeCurrencyUsed[costId].amount += cost.amount;
      });
    }
  });
  return upgradeCurrencyUsed;
};

export const itemKey = item => {
  const key = _.join([
    item.id,
    item.context || 0,
    item.crafted_stats || 0,
    item.bonus_id || 0,
    item.enchant_id || 0,
    item.gem_id || 0,
    item.titan_disc_id || 0,
  ], '-');

  return key;
};

export const getIncorrectMainStatPieces = character => {
  if (!character) {
    return null;
  }

  const sInfo = getCharacterSpecInfo(character);

  if (!sInfo) {
    return null;
  }

  const invalidGear = _.filter(
    character.items,
    item => item?.stats && itemHasAnyMainStat(item) && !itemHasMainStat(item, sInfo.mainStat),
  );

  return invalidGear;
};

